import { useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { FormCampoTipo, TipoArquivo } from "@workflow/types";
import { buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { arquivoService } from "@/services/api/ArquivoService";
import { DadosConclusaoDTO, FormCampoDTO, FormSchemaDTO } from "@/types/conclusao.type";
import { FormRenderer } from "./FormRenderer";
import { condicaoSatisfeita } from "./conclusao-form-utils";

interface ConcluirFormularioInlineProps {
  schema: FormSchemaDTO;
  isConcluindo: boolean;
  onConfirm: (dados: DadosConclusaoDTO) => void;
}

/**
 * Formulário de conclusão renderizado **inline** no painel de conclusão (sem
 * dialog). Coleta os dados e resolve o upload em duas fases: cada campo ARQUIVO
 * é enviado e substituído pelo ArquivoRef ({ id, nome }) antes de concluir. O
 * domínio revalida no backend.
 *
 * O botão de concluir é **dinâmico**: enquanto faltam campos obrigatórios
 * (considerando os condicionais `exibirSe`), ele fica bloqueado (vermelho + ⚠) e
 * um tooltip lista o que falta — não fica `disabled`, senão o tooltip não
 * dispararia no hover (mesmo padrão do "Próximo" dos wizards).
 */
export function ConcluirFormularioInline({
  schema,
  isConcluindo,
  onConfirm,
}: Readonly<ConcluirFormularioInlineProps>) {
  const form = useForm<Record<string, unknown>>({ defaultValues: {} });
  const [isUploading, setIsUploading] = useState(false);

  const isBusy = isUploading || isConcluindo;

  // Pendências reativas: campos obrigatórios e visíveis que ainda estão vazios.
  const values = useWatch({ control: form.control });
  const pendentes = useMemo(() => camposPendentes(schema, values ?? {}), [schema, values]);
  const isBlocked = pendentes.length > 0;

  const onSubmit = form.handleSubmit(async (raw) => {
    // Trava de segurança: clicar bloqueado marca os campos pendentes e não conclui.
    if (isBlocked) {
      pendentes.forEach((campo) =>
        form.setError(campo.nome, { type: "required", message: `${campo.label} é obrigatório.` }),
      );
      return;
    }

    try {
      setIsUploading(true);
      const dados = await resolverDados(schema, raw);
      onConfirm(dados);
    } catch (error) {
      console.error("Erro ao preparar dados de conclusão.", error);
      toast.error("Não foi possível enviar os arquivos. Tente novamente.");
    } finally {
      setIsUploading(false);
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        {schema.descricao && <p className="text-sm text-muted-foreground">{schema.descricao}</p>}

        <FormRenderer schema={schema} />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              type="submit"
              disabled={isBusy}
              className={cn(
                buttonVariants({
                  variant: isBlocked && !isBusy ? "destructive" : "default",
                }),
                "w-full",
                !isBlocked && !isBusy && "bg-emerald-600 text-white hover:bg-emerald-700",
              )}
            >
              {isBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUploading ? "Enviando..." : "Concluindo..."}
                </>
              ) : isBlocked ? (
                <>
                  <AlertCircle className="mr-2 h-4 w-4" />
                  {pendentes.length} campo{pendentes.length === 1 ? "" : "s"} a preencher
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Concluir tarefa
                </>
              )}
            </TooltipTrigger>
            {isBlocked && !isBusy && (
              <TooltipContent side="top" className="max-w-[260px]">
                <p className="mb-1 flex items-center gap-1.5 font-medium">
                  <AlertCircle className="size-3.5 shrink-0" /> Preencha para concluir
                </p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs">
                  {pendentes.slice(0, 5).map((campo) => (
                    <li key={campo.nome}>{campo.label}</li>
                  ))}
                </ul>
                {pendentes.length > 5 && (
                  <p className="mt-1 text-xs opacity-80">e mais {pendentes.length - 5}…</p>
                )}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </form>
    </FormProvider>
  );
}

/** Campo vazio para fins de obrigatoriedade (string vazia, nulo ou lista vazia). */
function isVazio(valor: unknown): boolean {
  if (valor === undefined || valor === null || valor === "") return true;
  if (Array.isArray(valor)) return valor.length === 0;
  return false;
}

/** Campos obrigatórios e atualmente visíveis (`exibirSe`) que ainda estão vazios. */
function camposPendentes(schema: FormSchemaDTO, values: Record<string, unknown>): FormCampoDTO[] {
  return schema.campos.filter((campo) => {
    if (!campo.obrigatorio) return false;
    if (campo.exibirSe && !condicaoSatisfeita(campo.exibirSe, values[campo.exibirSe.campo]))
      return false;
    return isVazio(values[campo.nome]);
  });
}

/** Faz upload dos campos de arquivo e monta o objeto de dados de conclusão. */
async function resolverDados(
  schema: FormSchemaDTO,
  values: Record<string, unknown>,
): Promise<DadosConclusaoDTO> {
  const dados: DadosConclusaoDTO = {};

  for (const campo of schema.campos) {
    const valor = values[campo.nome];
    if (valor === undefined || valor === null || valor === "") continue;

    if (campo.tipo === FormCampoTipo.ARQUIVO && valor instanceof File) {
      const arquivo = await arquivoService.uploadArquivo(
        (campo.tipoArquivo as TipoArquivo) ?? TipoArquivo.TAREFA,
        valor,
      );
      dados[campo.nome] = { id: arquivo.id, nome: arquivo.nome };
      continue;
    }

    dados[campo.nome] = valor;
  }

  return dados;
}
